import React, { useState, useEffect, useCallback } from "react";
import { Plus, Edit2, Trash2, Save, X } from "lucide-react";
import { useAuth } from "../../contexts/AuthContext";
import Loader from "../../common_components/Loader";
import { NoticeStatus, UserRole } from "../../types/status";

interface NoticeFormData {
  _id?: string;
  id: string | number;
  title: string;
  date: string;
  type: string;
  link: string;
  module: string;
  year: number;
  status: NoticeStatus;
  isNew: boolean;
}

interface Notice extends NoticeFormData {
  _id: string;
}

const defaultFormData: NoticeFormData = {
  id: "",
  title: "",
  date: new Date().toISOString().split("T")[0],
  type: "Notice",
  link: "",
  module: "Student Admission",
  year: new Date().getFullYear(),
  status: NoticeStatus.PUBLISHED,
  isNew: true,
};

const NoticeManagement: React.FC = () => {
  const { apiRequest, isAdmin } = useAuth();
  const [notices, setNotices] = useState<Notice[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [error, setError] = useState("");

  const [formData, setFormData] = useState<NoticeFormData>(defaultFormData);

  // Pagination & Archive / Deleted state
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [showArchive, setShowArchive] = useState(false);
  const [showDeleted, setShowDeleted] = useState(false);

  const fetchNotices = useCallback(async (pageNum = page, showArc = showArchive, showDel = showDeleted) => {
    setIsLoading(true);
    try {
      const url = showDel
        ? `/notifications?page=${pageNum}&limit=10&showDeleted=true`
        : `/notifications?page=${pageNum}&limit=10&showArchive=${showArc}`;
      const data = await apiRequest(url);
      setNotices(data.notices || []);
      setTotalPages(data.totalPages || 1);
    } catch (err: unknown) {
      const e = err as { message?: string };
      setError(e.message || "Failed to fetch notices");
    } finally {
      setIsLoading(false);
    }
  }, [apiRequest, page, showArchive, showDeleted]);

  useEffect(() => {
    fetchNotices(page, showArchive, showDeleted);
  }, [fetchNotices, page, showArchive, showDeleted]);

  const handleInputChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>
  ) => {
    const target = e.target as HTMLInputElement;
    const value = target.type === "checkbox" ? target.checked : target.value;
    setFormData({ ...formData, [target.name]: value });
  };

  const handleEdit = (notice: Notice) => {
    setFormData({
      ...notice,
      date: new Date(notice.date).toISOString().split("T")[0],
      status: notice.status || NoticeStatus.PUBLISHED,
    });
    setIsEditing(true);
  };

  const handleDelete = async (id: string) => {
    if (!window.confirm("Are you sure you want to delete this notice?")) return;
    try {
      await apiRequest(`/notifications/${id}`, {
        method: "DELETE",
      });
      fetchNotices(page, showArchive, showDeleted);
    } catch (err: unknown) {
      const e = err as { message?: string };
      setError(e.message || "Failed to delete notice");
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      if (isEditing) {
        await apiRequest(`/notifications/${formData._id}`, {
          method: "PUT",
          body: JSON.stringify(formData),
        });
      } else {
        const payload = { ...formData };
        if (!payload.id) {
          payload.id = Math.floor(Math.random() * 1000000);
        }
        await apiRequest("/notifications", {
          method: "POST",
          body: JSON.stringify(payload),
        });
      }
      setIsEditing(false);
      setFormData(defaultFormData);
      fetchNotices(page, showArchive, showDeleted);
    } catch (err: unknown) {
      const e = err as { message?: string };
      setError(e.message || "Failed to save notice");
    }
  };

  return (
    <div className="max-w-6xl mx-auto p-6">
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-2xl font-bold text-slate-800">Notice Management</h2>
        <div className="flex items-center gap-3">
          {isAdmin && (
            <label className="inline-flex items-center cursor-pointer gap-2 bg-red-50 hover:bg-red-100 px-3 py-1.5 rounded-lg border border-red-200 transition-colors">
              <input
                type="checkbox"
                checked={showDeleted}
                onChange={(e) => {
                  setShowDeleted(e.target.checked);
                  if (e.target.checked) setShowArchive(false);
                  setPage(1);
                }}
                className="rounded text-red-600 focus:ring-red-500 border-red-300 w-4 h-4 cursor-pointer"
              />
              <span className="text-xs font-semibold text-red-700">Show Deleted Items</span>
            </label>
          )}
          <label className="inline-flex items-center cursor-pointer gap-2 bg-slate-100 hover:bg-slate-205 px-3 py-1.5 rounded-lg border border-slate-300 transition-colors">
            <input
              type="checkbox"
              checked={showArchive}
              onChange={(e) => {
                setShowArchive(e.target.checked);
                if (e.target.checked) setShowDeleted(false);
                setPage(1);
              }}
              className="rounded text-slate-900 focus:ring-slate-900 border-slate-350 w-4 h-4 cursor-pointer"
            />
            <span className="text-xs font-semibold text-slate-700">Show Archive</span>
          </label>
        </div>
      </div>

      {error && <div className="bg-red-100 text-red-700 p-3 rounded mb-4">{error}</div>}

      <div className="bg-white p-6 rounded-lg shadow-md mb-8">
        <h3 className="text-xl font-semibold mb-4">{isEditing ? "Edit Notice" : "Add New Notice"}</h3>
        <form onSubmit={handleSubmit} className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="col-span-2">
            <label className="block text-sm font-medium mb-1">Title</label>
            <input
              type="text"
              name="title"
              value={formData.title}
              onChange={handleInputChange}
              required
              className="w-full border border-slate-300 rounded p-2 focus:border-blue-500"
            />
          </div>

          <div>
            <label className="block text-sm font-medium mb-1">Date</label>
            <input
              type="date"
              name="date"
              value={formData.date}
              onChange={handleInputChange}
              required
              className="w-full border border-slate-300 rounded p-2 focus:border-blue-500"
            />
          </div>

          <div>
            <label className="block text-sm font-medium mb-1">Type</label>
            <select
              name="type"
              value={formData.type}
              onChange={handleInputChange}
              className="w-full border border-slate-300 rounded p-2 focus:border-blue-500"
            >
              <option value="Notice">Notice</option>
              <option value="Advertisement">Advertisement</option>
              <option value="Circular">Circular</option>
              <option value="Result">Result</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium mb-1">Module</label>
            <select
              name="module"
              value={formData.module}
              onChange={handleInputChange}
              className="w-full border border-slate-300 rounded p-2 focus:border-blue-500"
            >
              <option value="Student Admission">Student Admission</option>
              <option value="Faculty Recruitment">Faculty Recruitment</option>
              <option value="Non-Faculty Recruitment">Non-Faculty Recruitment</option>
              <option value="General">General</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium mb-1">Year</label>
            <input
              type="number"
              name="year"
              value={formData.year}
              onChange={handleInputChange}
              required
              className="w-full border border-slate-300 rounded p-2 focus:border-blue-500"
            />
          </div>

          <div>
            <label className="block text-sm font-medium mb-1">Status</label>
            <select
              name="status"
              value={formData.status || NoticeStatus.PUBLISHED}
              onChange={handleInputChange}
              className="w-full border border-slate-300 rounded p-2 focus:border-blue-500"
            >
              <option value={NoticeStatus.PUBLISHED}>{NoticeStatus.PUBLISHED}</option>
              <option value={NoticeStatus.ARCHIVED}>{NoticeStatus.ARCHIVED}</option>
            </select>
          </div>

          <div className="col-span-2">
            <label className="block text-sm font-medium mb-1">Link URL</label>
            <input
              type="url"
              name="link"
              value={formData.link}
              onChange={handleInputChange}
              required
              className="w-full border border-slate-300 rounded p-2 focus:border-blue-500"
            />
          </div>

          <div className="col-span-2 flex items-center">
            <input
              type="checkbox"
              name="isNew"
              checked={formData.isNew}
              onChange={handleInputChange}
              className="mr-2"
            />
            <label className="text-sm font-medium">Show 'New' Badge</label>
          </div>

          <div className="col-span-2 flex gap-3 mt-4">
            <button
              type="submit"
              className="px-6 py-2 bg-blue-600 text-white rounded shadow hover:bg-blue-700 flex items-center gap-2"
            >
              <Save className="w-4 h-4" /> {isEditing ? "Update Notice" : "Save Notice"}
            </button>
            {isEditing && (
              <button
                type="button"
                onClick={() => {
                  setIsEditing(false);
                  setFormData(defaultFormData);
                }}
                className="px-6 py-2 bg-gray-200 text-gray-800 rounded shadow hover:bg-gray-300 flex items-center gap-2"
              >
                <X className="w-4 h-4" /> Cancel
              </button>
            )}
          </div>
        </form>
      </div>

      <div className="bg-white rounded-lg shadow-md overflow-hidden">
        {isLoading ? (
          <Loader text="Loading notices..." />
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-left">
              <thead className="bg-slate-50 border-b">
                <tr>
                  <th className="p-4 font-semibold text-slate-600">Title</th>
                  <th className="p-4 font-semibold text-slate-600">Module</th>
                  <th className="p-4 font-semibold text-slate-600">Date</th>
                  <th className="p-4 font-semibold text-slate-600">Type</th>
                  <th className="p-4 font-semibold text-slate-600">Status</th>
                  <th className="p-4 font-semibold text-slate-600 text-right">Actions</th>
                </tr>
              </thead>
              <tbody>
                {notices.map((notice) => (
                  <tr key={notice._id} className="border-b hover:bg-slate-50">
                    <td className="p-4 font-medium text-slate-800">{notice.title}</td>
                    <td className="p-4 text-slate-600">
                      {notice.module}{" "}
                      <span className="text-xs bg-slate-200 px-2 py-1 rounded">{notice.year}</span>
                    </td>
                    <td className="p-4 text-slate-600">{new Date(notice.date).toLocaleDateString()}</td>
                    <td className="p-4 text-slate-600">
                      <span className="bg-blue-100 text-blue-800 px-2 py-1 rounded text-xs">{notice.type}</span>
                    </td>
                    <td className="p-4 text-slate-600">
                      <span className={`px-2 py-1 rounded text-xs ${
                        notice.status === NoticeStatus.DELETED
                          ? "bg-red-100 text-red-800 border border-red-200"
                          : notice.status === NoticeStatus.ARCHIVED
                          ? "bg-purple-100 text-purple-800 border border-purple-200"
                          : "bg-green-100 text-green-800 border border-green-200"
                      }`}>
                        {notice.status || "PUBLISHED"}
                      </span>
                    </td>
                    <td className="p-4 flex justify-end gap-2">
                      {notice.status !== NoticeStatus.ARCHIVED && notice.status !== NoticeStatus.DELETED && (
                        <>
                          <button
                            onClick={() => handleEdit(notice)}
                            className="p-2 text-blue-600 hover:bg-blue-50 rounded"
                            title="Edit"
                          >
                            <Edit2 className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => handleDelete(notice._id)}
                            className="p-2 text-red-600 hover:bg-red-50 rounded"
                            title="Delete"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </>
                      )}
                      {notice.status === NoticeStatus.DELETED && (
                        <span className="px-2 py-1 text-xs font-bold text-red-700 bg-red-100 border border-red-200 rounded">
                          DELETED
                        </span>
                      )}
                    </td>
                  </tr>
                ))}
                {notices.length === 0 && (
                  <tr>
                    <td colSpan={6} className="p-8 text-center text-slate-500">
                      No notices found
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {totalPages > 1 && (
        <div className="flex items-center justify-between border-t border-slate-100 pt-6 mt-6">
          <p className="text-xs text-slate-500 font-medium">
            Showing page <span className="font-semibold text-slate-800">{page}</span> of{" "}
            <span className="font-semibold text-slate-800">{totalPages}</span>
          </p>
          <div className="flex gap-2">
            <button
              disabled={page === 1}
              onClick={() => setPage(page - 1)}
              className="px-3 py-1.5 text-xs font-semibold bg-white border border-slate-200 rounded-lg hover:bg-slate-50 text-slate-700 disabled:opacity-50 disabled:pointer-events-none transition-colors"
            >
              Previous
            </button>
            <button
              disabled={page === totalPages}
              onClick={() => setPage(page + 1)}
              className="px-3 py-1.5 text-xs font-semibold bg-slate-900 border border-slate-900 rounded-lg hover:bg-slate-800 text-white disabled:opacity-50 disabled:pointer-events-none transition-colors"
            >
              Next
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default NoticeManagement;
